import React from 'react';
import { Link } from "react-router-dom";
import "./About.css";
import { useEffect, useRef, useState } from "react";

const CountUpMetric = ({ target, suffix, label }) => {
  const [count, setCount] = useState(0);
  const metricRef = useRef(null);

  useEffect(() => {
    const metric = metricRef.current;
    if (!metric) return undefined;

    const finishCount = () => setCount(target);
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      finishCount();
      return undefined;
    }

    let animationFrame;
    let startTime;
    const duration = 1400;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        observer.unobserve(metric);
        const animate = (timestamp) => {
          if (startTime === undefined) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          setCount(Math.floor(target * progress));

          if (progress < 1) {
            animationFrame = window.requestAnimationFrame(animate);
          }
        };

        animationFrame = window.requestAnimationFrame(animate);
      },
      { threshold: 0.35 }
    );

    observer.observe(metric);

    return () => {
      observer.disconnect();
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [target]);

  return (
    <div
      ref={metricRef}
      className="stat-number"
      aria-label={`${target}${suffix} ${label}`}
    >
      {count}
      {suffix}
    </div>
  );
};

const About = () => (
  <div className="about-page">
    <section className="services-hero-modern">
      <div className="container">
        <h1>About Tooraflex</h1>
        <p>
          Your partner in digital transformation. We combine technical
          expertise with creative thinking to deliver solutions that drive
          business growth.
        </p>
      </div>
    </section>

    <section className="section-padding">
      <div className="container">
        <div className="about-content">
          <div className="about-text">
            <h2>Our Story</h2>
            <p>
              Founded in 2015, Tooraflex began as a small team of passionate
              developers and designers with a shared vision: to make
              high-quality digital solutions accessible to businesses of all
              sizes. What started as a boutique web design studio has grown
              into a full-service digital solutions provider.
            </p>
            <p>
              Over the years, we&apos;ve helped hundreds of clients transform
              their digital presence, from startups to established
              enterprises. Our commitment to innovation, quality, and customer
              satisfaction has been the driving force behind our success.
            </p>
            <p>
              Today, we&apos;re proud to be a trusted partner for businesses
              looking to thrive in the digital world, offering everything from
              website design to complex data analytics solutions.
            </p>
          </div>
          <div className="about-stats">
            <div className="stat">
              <CountUpMetric
                target={150}
                suffix="+"
                label="projects completed"
              />
              <div className="stat-text">Projects Completed</div>
            </div>
            <div className="stat">
              <CountUpMetric
                target={98}
                suffix="%"
                label="client satisfaction"
              />
              <div className="stat-text">Client Satisfaction</div>
            </div>
            <div className="stat">
              <CountUpMetric target={50} suffix="+" label="happy clients" />
              <div className="stat-text">Happy Clients</div>
            </div>
            <div className="stat">
              <CountUpMetric target={8} suffix="+" label="years experience" />
              <div className="stat-text">Years Experience</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="section-padding about-principles-section">
      <div className="container">
        <div className="section-title about-section-title">
          <h2>Our Vision &amp; Mission</h2>
          <p>Guiding principles that drive everything we do</p>
        </div>

        <div className="about-principles">
          <article className="about-principle-card">
            <div className="about-principle-icon" aria-hidden="true">
              <i className="fas fa-eye"></i>
            </div>
            <h3>Our Vision</h3>
            <p>
              To be the leading digital solutions provider recognized for
              transforming businesses through innovative technology and
              exceptional service. We envision a world where every business,
              regardless of size, has access to the digital tools they need to
              succeed.
            </p>
          </article>

          <article className="about-principle-card">
            <div className="about-principle-icon" aria-hidden="true">
              <i className="fas fa-bullseye"></i>
            </div>
            <h3>Our Mission</h3>
            <p>
              To empower businesses with cutting-edge digital solutions that
              drive growth, enhance efficiency, and create meaningful
              connections with their audience. We achieve this by combining
              technical expertise with creative thinking and a deep
              understanding of our clients&apos; needs.
            </p>
          </article>

          <article className="about-principle-card">
            <div className="about-principle-icon" aria-hidden="true">
              <i className="fas fa-handshake"></i>
            </div>
            <h3>Our Values</h3>
            <ul>
              <li>
                <strong>Innovation:</strong> Continuously exploring new
                technologies and approaches
              </li>
              <li>
                <strong>Integrity:</strong> Honest communication and
                transparent processes
              </li>
              <li>
                <strong>Excellence:</strong> Commitment to quality in every
                project
              </li>
              <li>
                <strong>Collaboration:</strong> Working together with clients
                as partners
              </li>
              <li>
                <strong>Sustainability:</strong> Building solutions that stand
                the test of time
              </li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <section className="section-padding about-team-section">
      <div className="container">
        <div className="section-title about-section-title">
          <h2>Our Leadership Team</h2>
          <p>Experienced professionals guiding our company forward</p>
        </div>

        <div className="about-team-grid">
          <article className="about-team-card">
            <div className="about-team-icon" aria-hidden="true">
              <i className="fas fa-user-tie"></i>
            </div>
            <h3>Raymond Mashishi</h3>
            <p className="about-team-role">CEO &amp; Founder</p>
            <p>
              With over 15 years in the tech industry, Raymond leads our
              strategic direction and ensures we deliver exceptional value to
              our clients.
            </p>
          </article>

          <article className="about-team-card">
            <div className="about-team-icon" aria-hidden="true">
              <i className="fas fa-code"></i>
            </div>
            <h3>Makhutja Sebatjane</h3>
            <p className="about-team-role">COO</p>
            <p>
              Makhutja oversees all operations and leads our administration
              and development team in creating innovative, scalable solutions.
            </p>
          </article>

          <article className="about-team-card">
            <div className="about-team-icon" aria-hidden="true">
              <i className="fas fa-paint-brush"></i>
            </div>
            <h3>Michael Rodriguez</h3>
            <p className="about-team-role">Creative Director</p>
            <p>
              Michael ensures our designs are not only beautiful but also
              functional and aligned with our clients&apos; brand identities.
            </p>
          </article>
        </div>
      </div>
    </section>

    <section className="about-careers">
      <div className="container">
        <h2>Want to Join Our Team?</h2>
        <p>
          We&apos;re always looking for talented individuals who share our
          passion for technology and innovation.
        </p>
        <Link to="/contact" className="about-careers-button">
          View Career Opportunities
        </Link>
      </div>
    </section>
  </div>
);

export default About;
